// Utility helpers for downloading files/blobs in the browser.
// Uses a single hidden anchor element to trigger downloads safely.
import { httpClientRaw } from "api/httpClient";

let hiddenAnchor = null;

const getHiddenAnchor = () => {
	if (typeof document === "undefined") return null;
	if (hiddenAnchor) return hiddenAnchor;

	hiddenAnchor = document.createElement("a");
	hiddenAnchor.style.display = "none";
	document.body.appendChild(hiddenAnchor);
	return hiddenAnchor;
};

export const downloadBlob = (blob, filename) => {
	const anchor = getHiddenAnchor();
	if (!anchor) return;

	const objectUrl = window.URL.createObjectURL(blob);
	anchor.href = objectUrl;
	anchor.download = filename;
	anchor.click();
	window.URL.revokeObjectURL(objectUrl);
};

// Navigates the anchor instead of buffering the response in a blob, so the
// browser streams large files (e.g. APKs) straight to disk
export const downloadByNavigation = (url) => {
	const anchor = getHiddenAnchor();
	if (!anchor) return;

	anchor.href = url;
	anchor.download = "";
	anchor.click();
};

export const getResponseErrorMessage = async (res) => {
	try {
		const text = await res.text();
		if (!text) return res.statusText;

		const json = JSON.parse(text);
		return (
			json?.message ||
			json?.title ||
			json?.detail ||
			json?.explanation ||
			res.statusText
		);
	} catch (e) {
		return res.statusText;
	}
};

export const ensureResponseOk = async (res) => {
	if (res.ok) return res;

	const message = await getResponseErrorMessage(res);
	throw new Error(message || "ra.notification.http_error");
};

export const downloadFromUrl = async (url, filename) => {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`Download failed with status ${response.status}`);
	}

	const blob = await response.blob();
	downloadBlob(blob, filename);
};

export const downloadFile = async (url, filename, options = {}) => {
	const res = await httpClientRaw(url, { method: "GET", ...options });
	await ensureResponseOk(res);

	const blob = await res.blob();

	if (blob.size === 0) {
		throw new Error("Empty file received!");
	}

	// Extract filename from Content-Disposition header if not provided
	let finalFilename = filename;
	if (!finalFilename) {
		const contentDisposition = res.headers.get("Content-Disposition");
		if (contentDisposition) {
			const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
			if (filenameMatch && filenameMatch[1]) {
				finalFilename = filenameMatch[1];
			}
		}
		// Fallback to default if still no filename
		if (!finalFilename) {
			finalFilename = "download";
		}
	}

	downloadBlob(blob, finalFilename);
};

export const downloadFileWithNotification = (url, filename, notifyError, options = {}) => {
	return downloadFile(url, filename, options).catch(notifyError);
};
